/** @format */

import React, { FunctionComponent, useEffect, useState } from 'react';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import { TableBody, TableHead } from '@material-ui/core';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import I18n from '../../../components/Translate/I18n';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import ViewButton from '../../../components/Buttons/ViewButton';
import PlatformIpPoolNumber from '../../../interfaces/platform/PlatformIpPoolNumber';
import { IpStateSimpleHelper, IpTypeAddress } from '../../lib/search/IpStateSimpleHelper';
import i18next from 'i18next';
import OPL_STYLE from '../../../assets/themes/OPL/OplStyle';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';

export type StorageIpPartnerUserItemType = {
    classes: any;
    poolItems: Array<PlatformIpPoolNumber> | null;
    viewCallback: (iri: { uri: string }, event: any) => void;
    firstLoad: () => void;
    setActiveRow: (id: number) => void;
    activeRow: number;
    updateOrderList: (orders: Array<Order>) => void;
    updateFilterList: (filters: Array<Filter>) => void;
    filters: Array<Filter>;
    orders: Array<Order>;
    ipStateSimpleHelper: IpStateSimpleHelper;
};

export const StorageIpPartnerUserItem: FunctionComponent<StorageIpPartnerUserItemType> = ({
    poolItems,
    classes,
    viewCallback,
    updateOrderList,
    updateFilterList,
    setActiveRow,
    activeRow,
    firstLoad,
    filters,
    orders,
    ipStateSimpleHelper,
}): React.ReactElement | null => {
    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };
    const tableRowRoot = { root: classes.tableRowRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const [ipStateList, setIpStateList] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [ipTypeAddressList, setIpTypeAddressList] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);

    useEffect(() => {
        if (!ipStateList) {
            setIpStateList(ipStateSimpleHelper.getHeaderList());
        }
    }, [ipStateList, ipStateSimpleHelper]);

    useEffect(() => {
        if (ipTypeAddressList === null) {
            const c = new IpTypeAddress();
            setIpTypeAddressList(c.getStatus());
        }
    }, [ipTypeAddressList]);

    return (
        <React.Fragment>
            <TableHead>
                <TableRow>
                    <TableCell classes={tableStickyCommand} padding="none" align="left">
                        <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.ip"
                            fieldName="ip"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="string"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="simcard:list.msisdn"
                            fieldName="msisdn"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.imsi"
                            fieldName="imsi"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.ip_state"
                            fieldName="ipState"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="select"
                            selectComponent="headerCheckBoxSimpleList"
                            elementList={ipStateList}
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.ip_apn"
                            fieldName="definedApnName"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="string"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            title="pools:apn.ip_type_public_or_private"
                            textAlign="left"
                            fieldName="private"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="select"
                            selectComponent="headerCheckBoxSimpleList"
                            elementList={ipTypeAddressList}
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {poolItems?.map((item) => {
                    let rowActiveColor = 'white';
                    if (activeRow === item.id) {
                        rowActiveColor = OPL_STYLE.TABLE.ACTIVE_ROW;
                    }

                    let ipStateName = ipStateSimpleHelper.getNameByMap(item.ipState);
                    if (ipStateName) {
                        ipStateName = i18next.t(ipStateName).toString();
                    }

                    return (
                        <TableRow
                            classes={tableRowRoot}
                            key={'key' + item.id}
                            hover
                            onClick={(): void => {
                                setActiveRow(item.id);
                            }}
                            style={{ backgroundColor: rowActiveColor }}
                        >
                            <TableCell classes={tableControlStickyHeader} padding="none" align="left" style={{ height: '40px' }}>
                                <ViewButton
                                    callback={viewCallback}
                                    callbackParam={{ uri: item['@id'] }}
                                    tooltipTitle="pools:ip.ip_view"
                                    tooltipPlacement="bottom-start"
                                />
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.ip}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.msisdn}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.imsi}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {ipStateName}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.definedApnName}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.private ? <I18n trans="pools:apn.header_private" /> : <I18n trans="pools:apn.header_public" />}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </React.Fragment>
    );
};
